import {
  Link,
  createFileRoute,
  stripSearchParams,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { useId } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import TabBar, { TabPanel, type TabBarOption } from '@/components/ui/TabBar'
import TitleRow from '@/components/ui/TitleRow'
import { useOnline } from '@/hooks/useOnline'
import { usePurchasedCourses, useSuspenseMyLearning } from '@/features/courses/api/queries'
import { resolveViewState } from '@/lib/view-state'
import { LEARN_PAGE } from '@/screens/courses/components/learn/learn-page'
import CoursePaymentsSection from '@/screens/courses/components/my-learning/CoursePaymentsSection'
import MyLearningCourseList from '@/screens/courses/components/my-learning/MyLearningCourseList'
import MyLearningSkeleton from '@/screens/courses/components/my-learning/MyLearningSkeleton'
import PickUpNextShelf from '@/screens/courses/components/my-learning/PickUpNextShelf'
import MyLearningStatTiles from '@/screens/courses/components/my-learning/MyLearningStatTiles'
import PurchasesTable from '@/screens/courses/components/my-learning/PurchasesTable'
import { revealMotion } from '@/lib/motion/reveal'
import { cn } from '@/lib/utils'
import {
  MY_LEARNING_SEARCH_DEFAULTS,
  MY_LEARNING_TABS,
  loadMyLearning,
  myLearningSearchSchema,
} from '@/screens/courses/MyLearningScreen.loader'

const TITLE = 'My Learning'

/**
 * The section tab strip names this page from `lg` up, so its `h1` is
 * screen-reader only there; below `lg` there is no strip and it shows. The
 * intro line under it shows at every width (rest:ln, the mock's `ln-intro`).
 */
const HEADER = <TitleRow title={TITLE} subtitle="Courses you own and how far you are in each." />

const BROWSE_COURSES = (
  <Link to="/courses" className={stateActionClass('section')}>
    Browse courses
  </Link>
)

function MyLearningPending() {
  return (
    <ScreenLayout
      {...LEARN_PAGE}
      pending
      pendingLabel="Loading your learning…"
      header={HEADER}
      pageTitle={TITLE}
    >
      <MyLearningSkeleton />
    </ScreenLayout>
  )
}

function MyLearningError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} fullWidth header={HEADER} title="Your learning did not load" />
}

export const Route = createFileRoute('/_app/courses/my-learning')({
  validateSearch: myLearningSearchSchema,
  search: { middlewares: [stripSearchParams(MY_LEARNING_SEARCH_DEFAULTS)] },
  loader: loadMyLearning,
  pendingComponent: MyLearningPending,
  errorComponent: MyLearningError,
  component: MyLearningScreen,
})

/** The Purchases tab: its own (non-suspense) read, since a course write from the player must not reopen this whole route's suspense. */
function PurchasesPanel() {
  const query = usePurchasedCourses()
  const online = useOnline()
  const view = resolveViewState(query, { online })

  if (view === 'failed' || view === 'offline') {
    return (
      <EmptyState
        kind={view}
        size="section"
        framed
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    )
  }

  return (
    <PurchasesTable
      rows={query.data ?? []}
      isLoading={view === 'loading'}
      emptyState={
        <EmptyState
          title="You haven't purchased a course yet"
          description="Courses you buy will show up here."
          action={BROWSE_COURSES}
        />
      }
    />
  )
}

/**
 * Learn · My learning — the member's own progress: the lectures they have
 * watched, the courses themselves split by whether they are done, the
 * purchases behind them and the payments that bought them, and a shelf of
 * what to pick up next.
 *
 * `tab` is the only thing this page reads from the URL; the rollup itself
 * (`useSuspenseMyLearning`) is the route's MAIN data, already loaded by the
 * time this renders, so switching tabs never re-suspends the page — only the
 * Purchases tab's own reads (purchases, then payments) are fetched (or shown
 * from the loader's prefetch) the first time a member opens it.
 */
function MyLearningScreen() {
  const idBase = useId()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data } = useSuspenseMyLearning()

  const changeTab = (value: string) => {
    const tab = MY_LEARNING_TABS.find((option) => option === value)
    if (tab === undefined) return
    void navigate({ search: (prev) => ({ ...prev, tab }) })
  }

  const inProgress = data.courses.filter((row) => !row.finished)
  const finished = data.courses.filter((row) => row.finished)
  const noCoursesAtAll = data.summary.coursesTotal === 0

  // The counts are the lists' own, so a tab and what it opens never disagree.
  const tabOptions: readonly TabBarOption[] = [
    { value: 'in-progress', label: `In Progress (${String(inProgress.length)})` },
    { value: 'finished', label: `Finished (${String(finished.length)})` },
    { value: 'purchases', label: 'Purchases' },
  ]

  return (
    <ScreenLayout {...LEARN_PAGE} header={HEADER} pageTitle={TITLE}>
      <MyLearningStatTiles summary={data.summary} />
      <TabBar
        options={tabOptions}
        value={search.tab}
        onChange={changeTab}
        ariaLabel="My learning"
        idBase={idBase}
        stripClassName="gap-8"
      />
      <TabPanel
        key={search.tab}
        idBase={idBase}
        value={search.tab}
        className={cn('flex flex-col gap-6', revealMotion('fade-up').className)}
      >
        {search.tab === 'in-progress' ? (
          <MyLearningCourseList
            rows={inProgress}
            emptyTitle={noCoursesAtAll ? "You haven't started a course yet" : 'Nothing in progress'}
            emptyDescription={
              noCoursesAtAll
                ? 'Pick a course and your progress will show up here.'
                : 'Every course you have is finished — nice work.'
            }
            emptyAction={BROWSE_COURSES}
          />
        ) : search.tab === 'finished' ? (
          <MyLearningCourseList
            rows={finished}
            emptyTitle="No finished courses yet"
            emptyDescription="Courses you complete will show up here."
            emptyAction={BROWSE_COURSES}
          />
        ) : (
          <>
            <PurchasesPanel />
            <CoursePaymentsSection />
          </>
        )}
      </TabPanel>
      {search.tab === 'purchases' ? null : (
        <PickUpNextShelf ownedIds={new Set(data.courses.map((row) => row.course.id))} />
      )}
    </ScreenLayout>
  )
}
